import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import axiosInstance from "api/axiosInstance";
import { API_ENDPOINTS } from "api/endpoints";
import type { AdminVendor, VendorsState } from "types/vendor";

type ApiResponse<T> = {
  success?: boolean;
  message?: string;
  data?: T;
};

const getApiErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ message?: string }>;

    if (!axiosError.response) {
      return "Network error. Please try again.";
    }

    return (
      axiosError.response.data?.message ||
      "Something went wrong. Please try again."
    );
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Something went wrong. Please try again.";
};

const extractVendorList = (responseData: unknown): AdminVendor[] => {
  if (Array.isArray(responseData)) {
    return responseData as AdminVendor[];
  }

  if (!responseData || typeof responseData !== "object") {
    return [];
  }

  const data = responseData as {
    data?: unknown;
    vendors?: unknown;
    results?: unknown;
  };

  if (Array.isArray(data.data)) return data.data as AdminVendor[];
  if (Array.isArray(data.vendors)) return data.vendors as AdminVendor[];
  if (Array.isArray(data.results)) return data.results as AdminVendor[];

  return [];
};

export const fetchVendors = createAsyncThunk<
  AdminVendor[],
  void,
  { rejectValue: string }
>("vendors/fetchVendors", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<ApiResponse<AdminVendor[]>>(
      API_ENDPOINTS.vendors.list
    );

    if (response.data.success === false) {
      return rejectWithValue(
        response.data.message || "Unable to fetch vendors."
      );
    }

    return extractVendorList(response.data);
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error));
  }
});

const initialState: VendorsState = {
  vendors: [],
  loading: false,
  error: null,
};

const vendorsSlice = createSlice({
  name: "vendors",
  initialState,
  reducers: {
    clearVendorsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVendors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchVendors.fulfilled,
        (state, action: PayloadAction<AdminVendor[]>) => {
          state.vendors = action.payload;
          state.loading = false;
        }
      )
      .addCase(fetchVendors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Unable to fetch vendors.";
      });
  },
});

export const { clearVendorsError } = vendorsSlice.actions;
export default vendorsSlice.reducer;
